import React, { useEffect, useState } from "react";
import { Modal, Button, Row, Col, Card, Badge, Tabs, Tab } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../store";
import { fetchTokenUsage, fetchChatbotById } from "../../../features/ai_chatbots/aiChatbotsSlice";
import { useTranslation } from "react-i18next";
import { TokenUsage } from "../../../features/ai_chatbots/aiChatbotsService";
import { formatDate } from "../../../utils/dateUtils";

interface AIChatbotDetailModalProps {
  show: boolean;
  onHide: () => void;
  chatbotId?: number | null;
}

const AIChatbotDetailModal: React.FC<AIChatbotDetailModalProps> = ({ show, onHide, chatbotId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { selectedChatbot, tokenUsage, isLoading } = useSelector((state: RootState) => state.aiChatbots);
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    if (show && chatbotId) {
      dispatch(fetchChatbotById(chatbotId));
    }
  }, [show, chatbotId, dispatch]);

  useEffect(() => {
    if (show && selectedChatbot && activeTab === "usage") {
      dispatch(fetchTokenUsage({ id: selectedChatbot.id, skip: 0, limit: 100 }));
    }
  }, [show, selectedChatbot, activeTab, dispatch]);



  if (!selectedChatbot) {
    return null;
  }

  const calculateTotalTokens = (usages: TokenUsage[]) => {
    return usages.reduce((total, usage) => total + usage.token_count, 0);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{selectedChatbot.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || "details")}
          className="mb-3"
        >
          <Tab eventKey="details" title={t("Details")}>
            <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title>{t("Basic Information")}</Card.Title>
                    <div className="mb-2">
                      <strong>{t("Name")}:</strong> {selectedChatbot.name}
                    </div>
                    <div className="mb-2">
                      <strong>{t("Status")}:</strong>{" "}
                      <Badge bg={selectedChatbot.is_active ? "success" : "danger"}>
                        {selectedChatbot.is_active ? t("active") : t("inactive")}
                      </Badge>
                    </div>
                    <div className="mb-2">
                      <strong>{t("Model")}:</strong> {selectedChatbot.model}
                    </div>
                    <div className="mb-2">
                      <strong>{t("Created At")}:</strong>{" "}
                      {formatDate(selectedChatbot.created_at)}
                    </div>
                    <div className="mb-2">
                      <strong>{t("Updated At")}:</strong>{" "}
                      {formatDate(selectedChatbot.updated_at)}
                    </div>
                  </Card.Body>
                </Card>

                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title>{t("Integration")}</Card.Title>
                    <div className="mb-2">
                      <strong>{t("Widget Key")}:</strong> {selectedChatbot.widget_key}
                    </div>
                    <div className="mb-2">
                      <strong>{t("Allowed Domains")}:</strong>
                      <ul className="ps-4">
                        {selectedChatbot.allowed_domains && selectedChatbot.allowed_domains.length > 0 ? (
                          selectedChatbot.allowed_domains.map((domain: string, index: number) => (
                            <li key={index}>{domain}</li>
                          ))
                        ) : (
                          <li>{t("All domains allowed")}</li>
                        )}
                      </ul>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title>{t("Content")}</Card.Title>
                    <div className="mb-2">
                      <strong>{t("Description")}:</strong>
                      <p className="mb-2">{selectedChatbot.description || t("No description")}</p>
                    </div>
                    <div className="mb-2">
                      <strong>{t("Initial Message")}:</strong>
                      <p className="mb-2">{selectedChatbot.initial_message || t("No initial message")}</p>
                    </div>
                    {selectedChatbot.settings?.assistant_profile?.persona && (
                      <div className="mb-2">
                        <strong>{t("Assistant Persona")}:</strong>
                        <p className="mb-2">{selectedChatbot.settings.assistant_profile.persona}</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>

                {selectedChatbot.general_learning_set && (
                  <Card className="mb-3">
                    <Card.Body>
                      <Card.Title>{t("Knowledge Base")}</Card.Title>
                      <div className="mb-2">
                        <strong>{t("General Learning Set")}:</strong>
                        <p className="mb-2">{selectedChatbot.general_learning_set}</p>
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>
          </Tab>
          <Tab eventKey="usage" title={t("Token Usage")}>
            {isLoading ? (
              <div className="text-center py-4">{t("Loading...")}</div>
            ) : (
              <>
                <div className="mb-3">
                  <Card>
                    <Card.Body>
                      <Card.Title>{t("Usage Summary")}</Card.Title>
                      <div className="d-flex justify-content-between mb-2">
                        <div>
                          <strong>{t("Total Usage")}:</strong>{" "}
                          {calculateTotalTokens(tokenUsage)} {t("tokens")}
                        </div>
                        <div>
                          <strong>{t("Interactions")}:</strong> {tokenUsage.length}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
                <div className="table-responsive">
                  <table className="table table-striped table-bordered">
                    <thead>
                      <tr>
                        <th>{t("Date")}</th>
                        <th>{t("Session ID")}</th>
                        <th>{t("Model")}</th>
                        <th>{t("Type")}</th>
                        <th>{t("Tokens")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tokenUsage.length > 0 ? (
                        tokenUsage.map((usage) => (
                          <tr key={usage.id}>
                            <td>{formatDate(usage.created_at)}</td>
                            <td>
                              <span className="text-truncate d-inline-block" style={{ maxWidth: "100px" }}>
                                {usage.session_id}
                              </span>
                            </td>
                            <td>{usage.model_name}</td>
                            <td>{usage.operation_type}</td>
                            <td>{usage.token_count}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center">
                            {t("No token usage data available")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t("Close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AIChatbotDetailModal;